import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import {
  KSIEGOWOSC_AI_CTA_LABEL,
  KSIEGOWOSC_AI_DATE_MODIFIED,
  KSIEGOWOSC_AI_FAQ,
  KSIEGOWOSC_AI_FINAL_CTA,
  KSIEGOWOSC_AI_HERO,
  KSIEGOWOSC_AI_PRICE_LINE,
  KSIEGOWOSC_AI_RELATED_LINKS,
  KSIEGOWOSC_AI_SECTIONS,
  KSIEGOWOSC_AI_SEO,
  KSIEGOWOSC_AI_URL,
  KSIEGOWOSC_AI_UTM_CAMPAIGN,
  ksiegowoscAiStateAsOf,
  type KsiegowoscAiLink,
} from '@bookkeeper/shared/ksiegowosc-ai';
import { SeoHead } from '@/components/SeoHead';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { CookieConsent } from '@/components/CookieConsent';
import { ComparisonTable } from '@/components/ExplainerLayout';
import { AccuracyModule, KSIEGOWOSC_AI_ACCURACY_MODULE_ENABLED } from '@/components/AccuracyModule';
import { Button } from '@/components/ui/button';
import { useHydrated } from '@/hooks/useHydrated';
import { useSignupEntityType } from '@/hooks/useSignupUrl';
import { useTranslation } from '@/i18n/i18n';
import { buildSignUpUrl } from '@/lib/auth-url';
import { trackLandingCtaClicked } from '@/lib/analytics';

const PAGE_SLUG = 'ksiegowosc-ai';

/**
 * `/kalkulatory/*`, `/blog/*` and the pricing hash belong to other apps or to the home page, so
 * they are plain anchors; every other target is a route of this app.
 */
function isRouterLink(href: string): boolean {
  return !(href.startsWith('/kalkulatory/') || href.startsWith('/blog/') || href.startsWith('/#'));
}

function PageLink({ link, className }: { link: KsiegowoscAiLink; className: string }) {
  return isRouterLink(link.href) ? (
    <Link to={link.href} className={className}>
      {link.label}
    </Link>
  ) : (
    <a href={link.href} className={className}>
      {link.label}
    </a>
  );
}

/**
 * The CTA's own campaign UTMs are static, so the prerender renders them; the visitor's first-touch
 * attribution lives in browser storage and joins after hydration (same as ExplainerLayout).
 */
function useCampaignSignupUrl(slot: 'hero' | 'footer') {
  const { locale } = useTranslation();
  const entityType = useSignupEntityType({ entityType: 'jdg' });
  const hydrated = useHydrated();

  return useMemo(
    () =>
      buildSignUpUrl(
        locale,
        `?utm_source=site&utm_medium=cta&utm_campaign=${KSIEGOWOSC_AI_UTM_CAMPAIGN}&utm_content=${slot}`,
        {
          entityType,
          source: `ksiegowosc_ai_${slot}`,
          placement: slot,
          page: PAGE_SLUG,
          firstTouch: hydrated,
        }
      ),
    [entityType, locale, hydrated, slot]
  );
}

function SignupButton({ slot, href }: { slot: 'hero' | 'footer'; href: string }) {
  return (
    <Button asChild size="lg" className="h-14 rounded-full px-8 text-base font-bold">
      <a
        href={href}
        onClick={() =>
          trackLandingCtaClicked(slot, KSIEGOWOSC_AI_CTA_LABEL, {
            page: PAGE_SLUG,
            campaign: KSIEGOWOSC_AI_UTM_CAMPAIGN,
          })
        }
      >
        {KSIEGOWOSC_AI_CTA_LABEL}
        <ArrowRight className="size-4" aria-hidden="true" />
      </a>
    </Button>
  );
}

/**
 * /ksiegowosc-ai: the page for "księgowość AI" and its variants (BOO-5256 GEO pack).
 *
 * Polish only: the /en and /uk routes render the same page and canonicalise to the Polish URL, as
 * the przewodniki do. All copy, the FAQ and the JSON-LD come from `@bookkeeper/shared/ksiegowosc-ai`;
 * the JSON-LD lives only in `ksiegowosc-ai.html` (a second copy from Helmet would duplicate every
 * block on a direct load), and src/test/ksiegowosc-ai.test.tsx pins that copy to this one.
 */
export default function KsiegowoscAi() {
  const heroUrl = useCampaignSignupUrl('hero');
  const footerUrl = useCampaignSignupUrl('footer');

  return (
    <div className="min-h-screen pt-16 md:pt-20">
      <SeoHead>
        <html lang="pl" />
        <title>{KSIEGOWOSC_AI_SEO.title}</title>
        <meta name="description" content={KSIEGOWOSC_AI_SEO.description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={KSIEGOWOSC_AI_URL} />
        <meta property="og:title" content={KSIEGOWOSC_AI_SEO.title} />
        <meta property="og:description" content={KSIEGOWOSC_AI_SEO.description} />
        <meta property="og:url" content={KSIEGOWOSC_AI_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pl_PL" />
      </SeoHead>

      <Navigation />

      <main className="bg-background">
        <nav
          aria-label="Ścieżka nawigacji"
          className="mx-auto max-w-3xl px-6 pt-10 text-sm text-muted-foreground md:px-8"
        >
          <ol className="flex flex-wrap items-center gap-1.5">
            <li>
              <Link to="/" className="transition-colors hover:text-primary">
                Bookeper
              </Link>
            </li>
            <li aria-hidden="true" role="presentation">
              <ChevronRight className="size-3.5" />
            </li>
            <li aria-current="page" className="text-foreground">
              {KSIEGOWOSC_AI_SEO.breadcrumbName}
            </li>
          </ol>
        </nav>

        {/* Hero: the AI-quotable answer comes straight after the H1. */}
        <header className="mx-auto max-w-3xl px-6 pb-10 pt-6 md:px-8">
          <h1 className="mb-6 text-3xl font-semibold leading-[1.15] tracking-tight text-foreground md:text-4xl lg:text-5xl">
            {KSIEGOWOSC_AI_HERO.h1}
          </h1>
          <p className="text-lg leading-relaxed text-foreground/80 md:text-xl">
            {KSIEGOWOSC_AI_HERO.lead}
          </p>
          <p className="mt-4 text-base leading-relaxed text-foreground/80">
            {KSIEGOWOSC_AI_HERO.productLimit}
          </p>
          <div className="mt-6 rounded-xl border border-border/60 bg-card p-5">
            <SignupButton slot="hero" href={heroUrl} />
            <p className="mt-3 text-sm font-medium text-foreground">{KSIEGOWOSC_AI_PRICE_LINE}</p>
            <p className="mt-3 text-sm">
              <a
                href={KSIEGOWOSC_AI_HERO.secondaryLink.href}
                className="text-primary underline underline-offset-4 hover:no-underline"
              >
                {KSIEGOWOSC_AI_HERO.secondaryLink.label}
              </a>
            </p>
          </div>
        </header>

        <div className="mx-auto max-w-3xl space-y-12 px-6 pb-8 md:px-8">
          {KSIEGOWOSC_AI_SECTIONS.map((section) => (
            <section key={section.id} aria-labelledby={section.id}>
              <h2
                id={section.id}
                className="mb-4 text-2xl font-semibold tracking-tight md:text-3xl"
              >
                {section.heading}
              </h2>
              <div className="space-y-4 text-base leading-relaxed text-foreground/80 md:text-lg">
                {section.paragraphs.map((paragraph, index) => (
                  <p key={`${section.id}-p${index}`}>{paragraph}</p>
                ))}
              </div>
              {section.bullets ? (
                <ul className="mt-4 list-disc space-y-2 pl-6 text-base leading-relaxed text-foreground/80 md:text-lg">
                  {section.bullets.map((bullet, index) => (
                    <li key={`${section.id}-b${index}`}>{bullet}</li>
                  ))}
                </ul>
              ) : null}
              {section.steps ? (
                <ol className="list-decimal space-y-2 pl-6 text-base leading-relaxed text-foreground/80 md:text-lg">
                  {section.steps.map((step, index) => (
                    <li key={`${section.id}-s${index}`}>{step}</li>
                  ))}
                </ol>
              ) : null}
              {/* Same part order as renderKsiegowoscAiMarkdown: table, links, then closing. */}
              {section.table ? (
                <ComparisonTable table={section.table} sectionId={section.id} />
              ) : null}
              {section.links ? (
                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {section.links.map((link) => (
                    <li key={link.href}>
                      <PageLink
                        link={link}
                        className="flex items-center justify-between rounded-xl border border-border/50 bg-card p-4 text-base font-medium text-foreground transition-colors hover:border-primary/30 hover:text-primary"
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
              {section.closing ? (
                <div className="mt-4 space-y-4 text-base leading-relaxed text-foreground/80 md:text-lg">
                  {section.closing.map((paragraph, index) => (
                    <p key={`${section.id}-c${index}`}>{paragraph}</p>
                  ))}
                </div>
              ) : null}
            </section>
          ))}

          {/* Off by default (BOO-5311): renders nothing, and no figure, until /skutecznosc is live. */}
          {KSIEGOWOSC_AI_ACCURACY_MODULE_ENABLED ? <AccuracyModule /> : null}
        </div>

        <section aria-labelledby="faq" className="mx-auto max-w-3xl px-6 py-12 md:px-8">
          <h2 id="faq" className="mb-6 text-2xl font-semibold tracking-tight md:text-3xl">
            Najczęstsze pytania
          </h2>
          <div className="space-y-6">
            {KSIEGOWOSC_AI_FAQ.map((item) => (
              <div key={item.q} className="rounded-xl border border-border/60 bg-card p-5">
                <h3 className="mb-2 text-lg font-semibold text-foreground">{item.q}</h3>
                <p className="text-base leading-relaxed text-foreground/80">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="zacznij" className="mx-auto max-w-3xl px-6 pb-12 md:px-8">
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-6">
            <h2 id="zacznij" className="mb-3 text-xl font-semibold md:text-2xl">
              {KSIEGOWOSC_AI_FINAL_CTA.heading}
            </h2>
            <p className="mb-5 text-base leading-relaxed text-foreground/80">
              {KSIEGOWOSC_AI_FINAL_CTA.body}
            </p>
            <SignupButton slot="footer" href={footerUrl} />
            <p className="mt-3 text-sm text-muted-foreground">
              {KSIEGOWOSC_AI_FINAL_CTA.microcopy}
            </p>
          </div>
        </section>

        <section aria-labelledby="powiazane" className="mx-auto max-w-3xl px-6 pb-12 md:px-8">
          <h2 id="powiazane" className="mb-5 text-xl font-semibold">
            Powiązane strony
          </h2>
          <ul className="space-y-3">
            {KSIEGOWOSC_AI_RELATED_LINKS.map((link) => (
              <li key={link.href}>
                <PageLink
                  link={link}
                  className="text-base font-medium text-foreground underline underline-offset-4 transition-colors hover:text-primary hover:no-underline"
                />
              </li>
            ))}
          </ul>
        </section>

        <p className="mx-auto max-w-3xl px-6 pb-20 text-sm text-muted-foreground md:px-8">
          <time dateTime={KSIEGOWOSC_AI_DATE_MODIFIED}>{ksiegowoscAiStateAsOf()}</time>
        </p>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
}
